import React, { useState, useEffect } from "react"
import { Box, Flex, Container } from "@radix-ui/themes"
import UnifiedHeader from "./UnifiedHeader"
import { useLanguage } from "../../../contexts/LanguageContext"

interface UnifiedLayoutProps {
  children: React.ReactNode
  sidebar: React.ReactNode
  title?: string
  subtitle?: string
  user?: {
    username?: string
    role?: string
  } | null
  onLogout?: () => void
  showNotifications?: boolean
  showUserMenu?: boolean
  headerChildren?: React.ReactNode
  className?: string
  profilePath?: string
  isRestaurant?: boolean
  restaurantId?: string
}

const UnifiedLayout: React.FC<UnifiedLayoutProps> = ({
  children,
  sidebar,
  title = "Dashboard",
  subtitle,
  user,
  onLogout,
  showNotifications = true,
  showUserMenu = true,
  headerChildren,
  className = "",
  profilePath = "/admin/profile",
  isRestaurant = false,
  restaurantId
}) => {
  const { language } = useLanguage()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [isDesktop, setIsDesktop] = useState(false)

  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 1024)
    }

    checkDesktop()
    window.addEventListener('resize', checkDesktop)
    return () => window.removeEventListener('resize', checkDesktop)
  }, [])

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen)
  }

  const closeSidebar = () => {
    setSidebarOpen(false)
  }

  const isRTL = language === 'ar'
  const headerHeight = 64 // Header height in pixels
  const sidebarWidth = 256 // Sidebar width in pixels (w-64 = 16rem = 256px)

  return (
    <Box
      className={`h-screen ${className}`}
      style={{ backgroundColor: 'var(--gray-2)' }}
    >
      {/* Fixed Header */}
      <Box
        className="fixed top-0 left-0 right-0 z-50"
        style={{ height: `${headerHeight}px` }}
      >
        <UnifiedHeader
          onMenuClick={toggleSidebar}
          title={title}
          subtitle={subtitle}
          user={user}
          onLogout={onLogout}
          showNotifications={showNotifications}
          showUserMenu={showUserMenu}
          profilePath={profilePath}
          isRestaurant={isRestaurant}
          restaurantId={restaurantId}
        >
          {headerChildren}
        </UnifiedHeader>
      </Box>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <Box
          className="fixed inset-0 z-40 lg:hidden"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            top: `${headerHeight}px`
          }}
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar - positioned below header and docked left/right based on language */}
      <Box
        className={`fixed w-64 transform transition-transform duration-300 ease-in-out ${isRTL ? 'right-0' : 'left-0'
          } ${sidebarOpen
            ? 'translate-x-0'
            : isRTL
              ? 'translate-x-full'
              : '-translate-x-full'
          } lg:translate-x-0`}
        style={{
          top: `${headerHeight}px`,
          bottom: 0,
          height: `calc(100vh - ${headerHeight}px)`,
          zIndex: 45
        }}
      >
        {React.cloneElement(sidebar as React.ReactElement, { onClose: closeSidebar })}
      </Box>

      {/* Main Content - with margin for sidebar on desktop */}
      <Box
        className="h-screen overflow-hidden"
        style={{
          paddingTop: `${headerHeight}px`
        }}
      >
        <Box
          className="h-full overflow-x-hidden overflow-y-auto transition-all duration-300"
          style={{
            backgroundColor: 'var(--gray-2)',
            marginLeft: isRTL ? 0 : (isDesktop ? `${sidebarWidth}px` : (sidebarOpen ? `${sidebarWidth}px` : '0')),
            marginRight: isRTL ? (isDesktop ? `${sidebarWidth}px` : (sidebarOpen ? `${sidebarWidth}px` : '0')) : 0
          }}
        >
          <Container size="4" px="6" py="8">
            {children}
          </Container>
        </Box>
      </Box>
    </Box>
  )
}

export default UnifiedLayout 